<template>
  <div class="price-build-up">
    <div class="price-build-up__head">
      <span />
      <span>{{ i18n.t('/AccessoryCatalog/ExclVat') }}</span>
      <span>{{ i18n.t('/AccessoryCatalog/InclVat') }}</span>
    </div>
    <template v-if="enriched">
      <!-- Without montage this is the only regular price, so it carries the
           strike the Adviesprijs row would otherwise show -->
      <div
        :class="[
          'price-build-up__row',
          { 'is-strike': actionActive && !hasMontage && showFullBuildUp },
        ]"
      >
        <div class="k">
          {{ i18n.t('/AccessoryCatalog/PriceRowPart') }}
          <small>{{ i18n.t('/AccessoryCatalog/PriceRowPartSub') }}</small>
        </div>
        <div class="v">{{ i18n.n(pon.PartsExcl, 'currency') }}</div>
        <div class="v">{{ i18n.n(pon.PartsIncl, 'currency') }}</div>
      </div>
      <template v-if="hasMontage && showFullBuildUp">
        <div class="price-build-up__row">
          <div class="k">
            {{ i18n.t('/AccessoryCatalog/PriceRowLabour') }}
            <small>{{ labourSub }}</small>
          </div>
          <div class="v">{{ i18n.n(pon.LabourExcl, 'currency') }}</div>
          <div class="v">{{ i18n.n(pon.LabourIncl, 'currency') }}</div>
        </div>
        <div
          :class="[
            'price-build-up__row',
            'price-build-up__row--sub',
            { 'is-strike': actionActive },
          ]"
        >
          <div class="k">
            {{ i18n.t('/AccessoryCatalog/PriceRowAdvice') }}
            <small>{{ i18n.t('/AccessoryCatalog/PriceRowAdviceSub') }}</small>
          </div>
          <div class="v">{{ i18n.n(pon.ConsumerExcl, 'currency') }}</div>
          <div class="v">{{ i18n.n(pon.ConsumerIncl, 'currency') }}</div>
        </div>
      </template>
      <div
        v-if="actionActive && showFullBuildUp"
        class="price-build-up__row price-build-up__row--action"
      >
        <div class="k">
          {{ i18n.t('/AccessoryCatalog/PriceRowAction') }}
          <small>{{ actionValiditySub }}</small>
        </div>
        <div class="v">{{ i18n.n(actionExcl, 'currency') }}</div>
        <div class="v">{{ i18n.n(actionIncl, 'currency') }}</div>
      </div>
    </template>
    <div v-else class="price-build-up__row">
      <div class="k">
        {{ i18n.t('/AccessoryCatalog/PriceRowAccessory') }}
        <small>{{ vatSub }}</small>
      </div>
      <div class="v">{{ i18n.n(priceExcl, 'currency') }}</div>
      <div class="v">{{ i18n.n(priceIncl, 'currency') }}</div>
    </div>
    <div class="price-build-up__row price-build-up__row--total">
      <div class="k">
        {{
          isPackageMemberLine
            ? i18n.t('/AccessoryCatalog/PriceRowPackageLine')
            : i18n.t('/AccessoryCatalog/PriceRowAutoline')
        }}
        <small>{{ totalRowSub }}</small>
      </div>
      <div class="v">{{ i18n.n(priceExcl, 'currency') }}</div>
      <div class="v">{{ i18n.n(priceIncl, 'currency') }}</div>
    </div>
  </div>
</template>

<script>
import {
  VAT_INCL,
  actionPrice,
  hasActiveAction,
  hasLabour,
  isEnriched,
  labourHours,
  toNumber,
} from '@/helpers/ponCatalog'
import dateHelper from '@/helpers/dateHelper'

export default {
  props: {
    /** The accessory on screen — a package member is one too */
    accessory: {
      type: Object,
      required: true,
    },
    /**
     * Which price the lines that have room for only one show. The table itself
     * always shows both; this is what the VAT sub-line names.
     */
    vat: {
      type: String,
      default: VAT_INCL,
    },
    /**
     * False for a package member: it is billed inside the package at a price
     * that excludes montage, so the montage, advice and action rows of its own
     * catalog entry do not add up to what it costs there.
     */
    showFullBuildUp: {
      type: Boolean,
      default: true,
    },
    /** Names the total row: the price in the package rather than the Autoline price */
    isPackageMemberLine: {
      type: Boolean,
      default: false,
    },
    /** Shared catalog clock; see mixins/catalogClock */
    now: {
      type: Date,
      default: null,
    },
  },
  data() {
    return {
      i18n: window.VUE_CLI_EXPORTS.plugins.i18n,
    }
  },
  computed: {
    clock() {
      return this.now || new Date()
    },
    pon() {
      return this.accessory.PonAccessoryCatalog || null
    },
    enriched() {
      return isEnriched(this.accessory)
    },
    actionActive() {
      return hasActiveAction(this.accessory, this.clock)
    },
    hasMontage() {
      return hasLabour(this.accessory)
    },
    action() {
      return actionPrice(this.accessory, this.clock)
    },
    actionExcl() {
      return this.action ? this.action.excl : null
    },
    actionIncl() {
      return this.action ? this.action.incl : null
    },
    priceExcl() {
      return toNumber(this.accessory.Price?.Excl)
    },
    priceIncl() {
      return toNumber(this.accessory.Price?.Incl)
    },
    actionValiditySub() {
      const range = {
        from: dateHelper.formatDate(this.pon.ActionValidFrom),
        to: dateHelper.formatDate(this.pon.ActionValidTo),
      }
      return this.hasMontage
        ? this.i18n.t('/AccessoryCatalog/PriceRowActionValidityMounted', range)
        : this.i18n.t('/AccessoryCatalog/PriceRowActionValidity', range)
    },
    labourSub() {
      return this.i18n.t('/AccessoryCatalog/PriceRowLabourSub', {
        hours: String(labourHours(this.accessory)).replace('.', ','),
        rate: this.i18n.n(this.pon.LabourRate, 'currency'),
      })
    },
    vatSub() {
      return this.i18n.t('/AccessoryCatalog/VatSub', {
        rate: toNumber(this.accessory.Price?.BtwRate),
      })
    },
    /** A member line that covers several pieces is still priced per piece here */
    totalRowSub() {
      if (!this.isPackageMemberLine || !(this.accessory.Count > 1)) return this.vatSub
      return `${this.i18n.t('/AccessoryCatalog/PerPiece')}, ${this.vatSub}`
    },
  },
}
</script>

<style lang="scss" scoped>
// No global font-size scale exists in styles/variables, so the sizes this table
// needs are named here rather than repeated as fractional literals.
$eyebrow-size: 11px;
$meta-size: 12px;
$row-size: 14px;
$total-size: 15px;

// Foreground on a --color-primary-light background. --color-primary is a brand
// accent meant for fills and borders: on its own light tint it drops to 1.9:1
// (SEAT) and 3.7:1 (CUPRA), both under the 4.5:1 AA floor. $text-color clears
// 9.3:1 on the darkest tint any of the six brands ships.
$on-primary-light: $text-color;
// $gray-dark is only 3.1:1 on that same tint; this one clears 5.7:1.
$on-primary-light-meta: $gray-darker;

.price-build-up {
  border: 1px solid $gray-light;
  border-radius: 10px;
  overflow: hidden;

  &__head,
  &__row {
    display: grid;
    grid-template-columns: minmax(0, 1fr) 92px 92px;
    gap: 10px;
    align-items: baseline;
    padding: 8px 14px;
  }

  &__head {
    background: $gray-lightest;
    border-bottom: 1px solid $gray-light;

    span {
      font-size: $eyebrow-size;
      font-weight: 700;
      letter-spacing: 0.05em;
      text-transform: uppercase;
      color: $gray-dark;
      text-align: right;

      &:first-child {
        text-align: left;
      }
    }
  }

  &__row {
    border-bottom: 1px solid $gray-lightest;
    font-size: $row-size;

    &:last-child {
      border-bottom: 0;
    }

    .v {
      text-align: right;
      font-variant-numeric: tabular-nums;
    }

    .k small {
      display: block;
      font-size: $meta-size;
      color: $gray-dark;
      margin-top: 2px;
      font-weight: 400;
    }

    &.is-strike .v {
      color: $gray-dark;
      text-decoration: line-through;
    }

    &--sub {
      background: $gray-lightest;
      font-weight: 700;
    }

    &--action {
      background: $white;
      font-weight: 700;
      color: $color-success-dark;

      .k small {
        color: $gray-dark;
      }
    }

    &--total {
      background: var(--color-primary-light);
      border-top: 1px solid $gray-light;
      font-weight: 700;
      color: $on-primary-light;
      font-size: $total-size;

      .k small {
        color: $on-primary-light-meta;
      }
    }
  }
}
</style>
